import type { ElementType } from "react";
import {
  Bell,
  Clock,
  Globe,
  PenLine,
  Pill,
  Settings2,
  Shield,
  User,
} from "lucide-react";
import type { ShellNavSection } from "../shell/types";

export type AccountSection =
  | "profile"
  | "availability"
  | "notifications"
  | "security"
  | "preferences"
  | "eprescribing"
  | "signature"
  | "public-profile";

export const ACCOUNT_SECTION_IDS: AccountSection[] = [
  "profile",
  "signature",
  "public-profile",
  "availability",
  "notifications",
  "security",
  "preferences",
  "eprescribing",
];

export const ACCOUNT_SECTION_META: Record<
  AccountSection,
  { label: string; description: string; icon: ElementType }
> = {
  profile: {
    label: "Profile",
    icon: User,
    description: "Name, credentials, contact info",
  },
  signature: {
    label: "Signature",
    icon: PenLine,
    description: "Draw or upload your e-signature for clinical notes",
  },
  availability: {
    label: "Availability",
    icon: Clock,
    description: "Working hours & schedule",
  },
  notifications: {
    label: "Notifications",
    icon: Bell,
    description: "Alerts & notification channels",
  },
  security: {
    label: "Security",
    icon: Shield,
    description: "Password, MFA & sessions",
  },
  preferences: {
    label: "Preferences",
    icon: Settings2,
    description: "Display, timezone & defaults",
  },
  eprescribing: {
    label: "ePrescribing",
    icon: Pill,
    description: "Prescriber profile & routing IDs",
  },
  "public-profile": {
    label: "Public Profile",
    icon: Globe,
    description: "Your patient-facing page, services & booking settings",
  },
};

export const ACCOUNT_NAV: ShellNavSection[] = [
  {
    label: "Account",
    items: ACCOUNT_SECTION_IDS.map((id) => ({
      href: `/account/${id}`,
      label: ACCOUNT_SECTION_META[id].label,
      icon: ACCOUNT_SECTION_META[id].icon,
    })),
  },
];

export const ACCOUNT_SIDEBAR_HEADER = {
  subtitle: "My Account",
  icon: User as ElementType,
};

export function isAccountSection(value: string | undefined): value is AccountSection {
  return !!value && ACCOUNT_SECTION_IDS.includes(value as AccountSection);
}
